/**
 * Partial baskets — ROADMAP 7.9, client side.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SERVER COMPUTES THE RISK, NOT THIS
 *
 * Unlike the day total (6.10), where the browser owns the marks, the risk of
 * a partial basket is computed server-side from the FILLED legs and arrives
 * as a finished sentence. There is no live price in it: `payoffShape` is
 * about expiry, and the question "is this still hedged" has the same answer
 * whatever the spot is doing. Recomputing it here would be a second
 * implementation of D19's one rule.
 *
 * A FRESH clientRequestId PER ATTEMPT, GENERATED ONCE
 *
 * It is minted when the retry button is pressed and reused for that attempt
 * only. That is what makes a double-click safe at the server — both carry the
 * same id — while a deliberate second attempt is genuinely new.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useState } from 'react'
import { api, qs } from '@/lib/api'
import { tickSocket } from '@/feed/tickSocket'

export interface TradeLegView {
  tradeLegId: string
  seq: number
  status: string
  side: string
  optionType: string
  strike: number
  lots: number
  qty: number
  netQty: number
  avgPrice: number
  retryable: boolean
}

export interface BasketRisk {
  unbounded: boolean
  maxLoss: number | null
  failedCount: number
  worseThanIntended: boolean
  sentence: string
}

export interface TradeView {
  tradeId: string
  status: string
  underlying: string
  openedAt: string
  legs: TradeLegView[]
  risk: BasketRisk | null
}

export type IssuesState =
  | { readonly kind: 'IDLE' }
  | { readonly kind: 'LOADING' }
  /** The read failed. NOT "no issues" — those must not look alike. */
  | { readonly kind: 'ERROR'; readonly why: string }
  | { readonly kind: 'READY'; readonly trades: TradeView[] }

/**
 * Validate at the boundary.
 *
 * `api.get<T>` is a type ASSERTION, not a check. A shape mismatch reaching
 * `.map` would take the Portfolio screen down with it, and this panel is a
 * guest on that screen.
 */
function asTrades(value: unknown): TradeView[] | null {
  if (typeof value !== 'object' || value === null) return null
  const v = value as { trades?: unknown }
  if (!Array.isArray(v.trades)) return null

  const legOf = (l: unknown): TradeLegView | null => {
    if (typeof l !== 'object' || l === null) return null
    const x = l as Partial<TradeLegView>
    if (typeof x.tradeLegId !== 'string' || typeof x.status !== 'string') return null
    return {
      tradeLegId: x.tradeLegId,
      seq: typeof x.seq === 'number' ? x.seq : 0,
      status: x.status,
      side: typeof x.side === 'string' ? x.side : '',
      optionType: typeof x.optionType === 'string' ? x.optionType : '',
      strike: typeof x.strike === 'number' ? x.strike : 0,
      lots: typeof x.lots === 'number' ? x.lots : 0,
      qty: typeof x.qty === 'number' ? x.qty : 0,
      netQty: typeof x.netQty === 'number' ? x.netQty : 0,
      avgPrice: typeof x.avgPrice === 'number' ? x.avgPrice : 0,
      retryable: x.retryable === true,
    }
  }

  const riskOf = (r: unknown): BasketRisk | null => {
    if (typeof r !== 'object' || r === null) return null
    const x = r as Partial<BasketRisk>
    if (typeof x.sentence !== 'string') return null
    return {
      unbounded: x.unbounded === true,
      maxLoss: typeof x.maxLoss === 'number' ? x.maxLoss : null,
      failedCount: typeof x.failedCount === 'number' ? x.failedCount : 0,
      worseThanIntended: x.worseThanIntended === true,
      sentence: x.sentence,
    }
  }

  return v.trades.flatMap((t) => {
    if (typeof t !== 'object' || t === null) return []
    const x = t as Partial<TradeView>
    if (typeof x.tradeId !== 'string' || !Array.isArray(x.legs)) return []
    return [{
      tradeId: x.tradeId,
      status: typeof x.status === 'string' ? x.status : 'OPENING',
      underlying: typeof x.underlying === 'string' ? x.underlying : '—',
      openedAt: typeof x.openedAt === 'string' ? x.openedAt : '',
      legs: x.legs.map(legOf).filter((l): l is TradeLegView => l !== null),
      risk: riskOf(x.risk),
    }]
  })
}

export interface TradeIssues {
  state: IssuesState
  /** Trades the user has to act on. Everything else is filtered out here. */
  issues: TradeView[]
  refresh: () => void
  retry: (tradeLegId: string) => Promise<void>
}

export function useTradeIssues(connectionId: string | null): TradeIssues {
  const [state, setState] = useState<IssuesState>({ kind: 'IDLE' })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!connectionId) {
      setState({ kind: 'IDLE' })
      return
    }
    let cancelled = false
    setState({ kind: 'LOADING' })
    void api
      .get<unknown>(`/trades${qs({ connectionId })}`)
      .then((r) => {
        if (cancelled) return
        const parsed = asTrades(r)
        setState(
          parsed === null
            ? { kind: 'ERROR', why: 'the trade list came back in a shape this build does not understand' }
            : { kind: 'READY', trades: parsed },
        )
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setState({ kind: 'ERROR', why: e instanceof Error ? e.message : 'could not read your trades' })
      })
    return () => {
      cancelled = true
    }
  }, [connectionId, nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  const retry = useCallback(async (tradeLegId: string): Promise<void> => {
    /*
     * Minted HERE, once per press. Reusing it across presses would make the
     * second a no-op; generating it server-side would make a double-click two
     * positions.
     */
    const clientRequestId = `retry-${tradeLegId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    await api.post('/trades/legs/' + tradeLegId + '/retry', { clientRequestId, simulationSessionId: tickSocket.getMarketContext().sessionId })
    setNonce((n) => n + 1)
  }, [])

  const issues = state.kind === 'READY' ? state.trades.filter((t) => t.status === 'ISSUE') : []

  return { state, issues, refresh, retry }
}
